import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { FileCode, Trash2, Copy, Download, Eye, Plus } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const GCodeLibrary = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openId, setOpenId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gcode/list`);
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!openId) {
      setOpenDoc(null);
      return;
    }

    const run = async () => {
      try {
        const res = await axios.get(`${API}/gcode/${openId}`);
        setOpenDoc(res.data);
      } catch (e) {
        toast.error('Failed to load file');
      }
    };

    run();
  }, [openId]);

  const downloadText = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this G-code file?')) return;
    try {
      await axios.delete(`${API}/gcode/${id}`);
      toast.success('Deleted');
      setItems((prev) => (prev || []).filter((x) => x.id !== id));
      if (openId === id) setOpenId(null);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const onDuplicate = (id) => {
    navigate('/tools/gcode-master', { state: { duplicateFromId: id } });
  };

  const title = useMemo(() => {
    if (!openDoc) return 'G-code';
    return openDoc.filename || 'G-code';
  }, [openDoc]);

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="gcode-library-page"
      style={bgStyle(IMAGES.GCODE_CAM)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      <div className="mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Profile</div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            My G-Code Library
          </h1>
          <p className="text-sm md:text-base text-[#a3a3a3] mt-2 max-w-2xl">
            Saved G-code files you’ve reviewed or optimized.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <Button 
          onClick={() => navigate('/tools/gcode-master')}
          className="bg-[#e67e22] hover:bg-[#d35400] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New / Edit in G-Code Master
        </Button>
      </div>

      <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
        <CardContent className="pt-6">
        {loading && <div className="text-sm text-[#a3a3a3]">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="text-center py-12">
            <FileCode className="w-16 h-16 mx-auto mb-4 text-[#3a3a3a]" />
            <h3 className="text-white font-bold text-lg mb-1">No G-Code Files Yet</h3>
            <p className="text-[#a3a3a3] text-sm mb-4">Files you create or review in G-Code Master will be saved here.</p>
            <Button
              onClick={() => navigate('/tools/gcode-master')}
              className="bg-[#c85a00] hover:bg-[#a84800] text-white"
            >
              Create your first G-code file
            </Button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-3">
            {items.map((x) => (
              <div key={x.id} className="border border-[#3a3a3a] rounded-sm p-4 bg-[#1f1f1f] hover:border-[#e67e22]/50 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white flex items-center gap-2">
                      <FileCode className="w-4 h-4 text-[#e67e22]" />
                      {x.filename}
                    </div>
                    <div className="text-xs text-[#a3a3a3] mt-1">
                      <span className="font-mono">{new Date(x.created_at).toLocaleString()}</span>
                      {x.controller_type && (
                        <span className="ml-3">Controller: <span className="font-mono text-[#e67e22]">{x.controller_type}</span></span>
                      )}
                      {x.machine_name && (
                        <span className="ml-3">Machine: <span className="font-mono text-[#d6d6d6]">{x.machine_name}</span></span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="border-[#3a3a3a] text-[#d6d6d6] hover:bg-white/10" onClick={() => setOpenId(x.id)}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button variant="outline" size="sm" className="border-[#3a3a3a] text-[#d6d6d6] hover:bg-white/10" onClick={() => onDuplicate(x.id)}>
                      <Copy className="w-3 h-3 mr-1" /> Duplicate
                    </Button>
                    <Button variant="outline" size="sm" className="border-[#3a3a3a] text-[#d6d6d6] hover:bg-white/10" onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/gcode/${x.id}`);
                        const txt = res.data.optimized_gcode || res.data.original_gcode || '';
                        downloadText(txt, res.data.filename || 'gcode.gcode');
                      } catch (e) {
                        toast.error('Download failed');
                      }
                    }}>
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                    <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => onDelete(x.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(openId)} onOpenChange={(v) => setOpenId(v ? openId : null)}>
        <DialogContent className="bg-[#1f1f1f] border border-[#3a3a3a] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white">{title}</DialogTitle>
          </DialogHeader>

          {!openDoc && <div className="text-sm text-[#a3a3a3]">Loading…</div>}

          {openDoc && (
            <Tabs defaultValue="optimized" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-[#2a2a2a]">
                <TabsTrigger value="optimized" className="data-[state=active]:bg-[#e67e22] data-[state=active]:text-white">Optimized</TabsTrigger>
                <TabsTrigger value="original" className="data-[state=active]:bg-[#e67e22] data-[state=active]:text-white">Original</TabsTrigger>
              </TabsList>
              <TabsContent value="optimized">
                <pre className="whitespace-pre-wrap font-mono text-sm text-[#d6d6d6] border border-[#3a3a3a] rounded-sm p-3 max-h-[60vh] overflow-auto bg-black/50">
                  {openDoc.optimized_gcode || 'No optimized G-code saved for this file.'}
                </pre>
              </TabsContent>
              <TabsContent value="original">
                <pre className="whitespace-pre-wrap font-mono text-sm text-[#d6d6d6] border border-[#3a3a3a] rounded-sm p-3 max-h-[60vh] overflow-auto bg-black/50">
                  {openDoc.original_gcode}
                </pre>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GCodeLibrary;
